import React, { useEffect, useState } from 'react';
import { X, Cpu, Check, Info, AlertCircle } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import { resolvePaymentConfig, paymentConfigOptionsFor } from '@platform/shared-types';
import {
  RubyTillPayRegisterFields, RtpayPairingCodePanel, RTPAY_CLASS, isValidRtpayPin, isValidTpn,
  type RtpayRegisterMode, type RtpayPairingCode,
} from './RubyTillPayRegisterFields';
import { hardwareApi } from '../../services/hardwareApi';
import SearchableDropdown from '../SearchableDropdown';

import { RubyGemMark } from '../RubyGemMark';
interface Props {
  tenantId: string;
  /** Location this device will live in — devices are pooled at the location,
   *  not bound to a terminal. Binding happens later when NMI Cloud Lane is
   *  enabled on a specific terminal and this device is picked from the dropdown. */
  locationId: string;
  locationName?: string;
  onClose: () => void;
  onRegistered: () => void;
}

/**
 * The selectable models come from the HARDWARE CATALOG (category
 * 'payment_device'), not a hardcoded list — a device must exist in
 * Device Configurations before it can be registered here.
 *
 * We send `model: modelName`. The backend
 * (super-admin-ext3/src/lib/device-models.ts) resolves payment routing by
 * matching that string and silently treats anything unknown as cloud, so a
 * catalog entry whose name it does not recognise is flagged below rather than
 * quietly registered as the wrong integration class.
 */
interface CatalogDevice {
  modelId: string;
  modelName: string;
  manufacturer: string;
  /** The device's chosen lane. Outranks both the profile and the model name. */
  paymentConfigId?: string;
  paymentProfile?: { deviceClass: string; connectionType: string; emvMode: string };
  paymentConfigOptions?: string[];
}

/** "How the till reaches it" — short names for the P-series routes. */
const ROUTE_TITLE: Record<string, { title: string; desc: string }> = {
  dejavoo_spin_usb: { title: 'SPIn USB', desc: 'Cable to the till through the base' },
  dejavoo_spin_cloud: { title: 'SPIn Cloud', desc: 'Dejavoo’s SPIn cloud' },
  [RTPAY_CLASS]: { title: 'RubyTill Pay', desc: 'Our app on the terminal, paired with a code' },
};

const RegisterPoiModal: React.FC<Props> = ({ tenantId, locationId, locationName, onClose, onRegistered }) => {
  const [deviceName, setDeviceName] = useState('');
  const [catalog, setCatalog] = useState<CatalogDevice[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [model, setModel] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [registrationCode, setRegistrationCode] = useState('');
  const [spinRegisterId, setSpinRegisterId] = useState('');
  const [spinAuthKey, setSpinAuthKey] = useState('');
  const [spinTpn, setSpinTpn] = useState('');
  /* The route the operator picked for a model that offers more than one.
     Empty = the model's default, which is all this dialog knew before. */
  const [routeId, setRouteId] = useState('');
  const [rtpayMode, setRtpayMode] = useState<RtpayRegisterMode>('charge_only');
  const [rtpayPin, setRtpayPin] = useState('');
  const [rtpayPaired, setRtpayPaired] = useState<{ deviceId: string; initial: RtpayPairingCode | null } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ poiDeviceId?: string; nmiSerialNumber?: string; deviceLicense?: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await hardwareApi.listModels({ category: 'payment_device', status: 'active' });
        if (cancelled) return;
        const items = (res.items || []) as unknown as CatalogDevice[];
        setCatalog(items);
        /*
          AUTO-SELECT THE ONE DEVICE THAT CAN ACTUALLY BE REGISTERED.

          Was "preselect when the catalog holds exactly one", which stopped
          firing the moment a second payment device existed — including ones
          that cannot be registered at all because nobody has given them a
          payment config. Counting the REGISTERABLE ones instead means the
          operator stops picking from a list of one, and it keeps working when
          the next P-series or D-series device is added: it is a property of
          the config, not a hardcoded model name.
        */
        const registerable = items.filter((c) => {
          const cfg = resolvePaymentConfig(c as any);
          return !!cfg && cfg.id !== 'none';
        });
        if (registerable.length === 1) setModel(registerable[0].modelName);
      } catch (e: any) {
        // No silent fallback to a hardcoded list: registering a device that is
        // not in the catalog is precisely what this screen exists to prevent.
        if (!cancelled) setCatalogError(e?.message || 'Could not load payment devices from the catalog');
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selected = catalog.find((c) => c.modelName === model);
  /*
    THE DEVICE'S CHOSEN LANE, which is what the backend now reads too.

    This used to be `getModelDef(model)` — an EXACT match on the display name
    against a hardcoded list. A catalog row called "Dejavoo P17 Android Pin
    Pad" is not 'Dejavoo P17', so it matched nothing and the service defaulted
    it to NMI cloud: a Dejavoo asked for a registration code it cannot produce.
    `resolvePaymentConfig` reads the chosen config first and keeps the name
    match only as a fallback for rows that predate the picker.
  */
  const defaultCfg = resolvePaymentConfig(selected as any);
  /* Same guard as DeviceSetupPanels' laneFor: a dev server whose pre-bundled
     shared-types predates the function degrades to the default route. */
  const routeOptions = selected && typeof paymentConfigOptionsFor === 'function'
    ? paymentConfigOptionsFor(selected as any)
    : (defaultCfg ? [defaultCfg] : []);
  const cfg = routeOptions.find((o) => o.id === routeId) || defaultCfg;
  const noConfig = !!model && (!cfg || cfg.id === 'none');
  const isSdk = cfg?.id === 'nmi_direct_vp3350';
  const isDejavooUsb = cfg?.id === 'dejavoo_spin_usb';
  const isRtpay = cfg?.id === RTPAY_CLASS;
  // Direct devices (VP3350 SDK, Dejavoo P-series USB) skip the cloud registration code;
  // RubyTill Pay pairs from the terminal with OUR code instead.
  const isDirect = isSdk || isDejavooUsb || isRtpay;

  const code = registrationCode.trim().toUpperCase();
  const modelReady = !!model && !noConfig;
  const canSave = !modelReady ? false : isRtpay
    ? Boolean(deviceName.trim() && serialNumber.trim() && isValidTpn(spinTpn) && isValidRtpayPin(rtpayPin))
    : isDejavooUsb
    ? Boolean(deviceName.trim() && serialNumber.trim() && spinRegisterId.trim() && spinAuthKey.trim() && /^\d{12}$/.test(spinTpn.trim()))
    : isDirect
      ? Boolean(deviceName.trim() && serialNumber.trim())
      : Boolean(deviceName.trim() && serialNumber.trim() && /^[A-Z0-9]{4,12}$/.test(code));

  const handleSave = async () => {
    if (!canSave || saving) return;
    try {
      setSaving(true);
      setError(null);
      const resp = await api.posNmi.registerEmvDeviceToLocation(tenantId, locationId, {
        deviceName: deviceName.trim(),
        model,
        paymentConfigId: cfg?.id,
        serialNumber: serialNumber.trim(),
        ...(isDirect ? { connectionType: cfg!.profile!.connectionType } : { registrationCode: code }),
        ...(isDejavooUsb ? { spinRegisterId: spinRegisterId.trim(), spinAuthKey: spinAuthKey.trim(), spinTpn: spinTpn.trim() } : {}),
        ...(isRtpay ? { spinTpn: spinTpn.trim(), rtpayMode, rtpayPin: rtpayPin.trim() } : {}),
      });
      if (isRtpay) {
        /* Stay open: the operator types the code on the terminal next. A code
           that fails to issue is not a failed registration — the panel offers
           "Get pairing code". */
        const newId = resp?.device?.deviceId || resp?.deviceId;
        let initial: RtpayPairingCode | null = null;
        if (newId) {
          try {
            const pc = await api.posNmi.newRtpayPairingCode(tenantId, newId);
            if (pc?.code) initial = { code: String(pc.code), expiresAt: pc.expiresAt };
          } catch { /* offered in the panel */ }
          setRtpayPaired({ deviceId: newId, initial });
        }
        toastService.success('Payment device registered — pair it on the terminal');
        if (!newId) setTimeout(() => onRegistered(), 800);
        return;
      }
      setResult({
        poiDeviceId: resp?.device?.terminalIdNmi,
        nmiSerialNumber: resp?.device?.nmiSerialNumber,
        deviceLicense: resp?.device?.deviceLicense,
      });
      toastService.success('Payment device registered and paired');
      // Brief delay so user sees the confirmation before the modal closes
      setTimeout(() => onRegistered(), 800);
    } catch (err: any) {
      const code = err?.error || err?.code;
      if (code === 'ENABLE_NMI_FIRST') {
        setError('Enable NMI Cloud Lane on the Payment Types tab first.');
      } else if (code === 'GATEWAY_NOT_CONFIGURED') {
        setError('Save NMI credentials (security key) on this location\'s gateway config before pairing a payment device.');
      } else if (code === 'ALREADY_PAIRED') {
        setError('This terminal already has a paired payment device. Use Replace to change it.');
      } else if (code === 'NMI_ID_IN_USE') {
        setError('This NMI device is already paired to another terminal.');
      } else if (code === 'NMI_REGISTER_FAILED') {
        setError(err?.message || 'NMI rejected the registration code. Double-check the code on the device screen and try again.');
      } else {
        setError(err?.message || 'Failed to register device');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center gap-3 p-5 border-b border-stone-100">
          <div className="w-10 h-10 rounded-lg bg-stone-900 flex items-center justify-center shrink-0">
            <Cpu size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-stone-900 font-semibold text-base">Register Payment Device</h2>
            <p className="text-stone-500 text-xs mt-0.5 truncate">
              Add to pool{locationName ? ` at ${locationName}` : ''}. Assign to a terminal later when enabling NMI Cloud Lane.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {catalogError && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-2">
              <AlertCircle size={14} className="text-red-600 shrink-0 mt-0.5" />
              <div className="text-xs text-red-800">
                <p className="font-semibold">Could not load payment devices</p>
                <p className="mt-0.5 text-red-700">{catalogError}</p>
              </div>
            </div>
          )}

          {!catalogLoading && !catalogError && catalog.length === 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
              <AlertCircle size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900">
                <p className="font-semibold">No payment devices in the catalog</p>
                <p className="mt-0.5 text-amber-800">
                  A payment device has to exist in the catalog before it can be registered to a location.
                </p>
                <a
                  href="/pos-config"
                  className="inline-block mt-1.5 font-semibold text-amber-900 underline underline-offset-2"
                >
                  Create one in Device Management →
                </a>
              </div>
            </div>
          )}

          {/* Guidance panel — cloud devices only */}
          {!!model && !isDirect && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
              <div className="flex items-start gap-2">
                <Info size={14} className="text-blue-600 shrink-0 mt-0.5" />
                <div className="text-xs text-blue-800 space-y-1">
                  <p className="font-semibold">How to get the registration code</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-blue-700">
                    <li>Power on the Ingenico E-7000 and connect it to the internet (Ethernet or Wi-Fi).</li>
                    <li>Wait for the idle screen to display a short alphanumeric code (e.g. <span className="font-mono">8AVJWV</span>).</li>
                    <li>Enter that code below. NMI binds the device to this terminal on save.</li>
                  </ol>
                  <p className="mt-1 text-[11px] text-blue-600">
                    The code changes every few minutes — re-read it from the device screen if it times out.
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* SDK guidance panel — VP3350 / Bluetooth readers */}
          {isSdk && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
              <div className="flex items-start gap-2">
                <Info size={14} className="text-blue-600 shrink-0 mt-0.5" />
                <div className="text-xs text-blue-800 space-y-1">
                  <p className="font-semibold">Bluetooth / USB reader — no registration code</p>
                  <p className="text-blue-700">
                    The VP3350 pairs to the POS tablet over Bluetooth or USB at the terminal.
                    Register it here to add it to the pool, then assign it to a terminal.
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* Dejavoo P17 — SPIn USB */}
          {isDejavooUsb && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
              <div className="flex items-start gap-2">
                <Info size={14} className="text-blue-600 shrink-0 mt-0.5" />
                <div className="text-xs text-blue-800 space-y-1">
                  <p className="font-semibold">Dejavoo P17 — USB, no registration code</p>
                  <p className="text-blue-700">
                    The P17 connects to the POS tablet over USB (via its P1/P18 base) and runs
                    SPIn USB. Set the terminal's TPN to SPIn Mode = USB in the iPOSpays portal.
                    Register it here to add it to the pool, then assign it to a terminal.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* RubyTill Pay — our app on the P-series terminal */}
          {isRtpay && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
              <div className="flex items-start gap-2">
                <Info size={14} className="text-blue-600 shrink-0 mt-0.5" />
                <div className="text-xs text-blue-800 space-y-1">
                  <p className="font-semibold">RubyTill Pay — pairs with a code, no SPIn setup</p>
                  <p className="text-blue-700">
                    Our own payment app runs on the terminal. Register it here, then type the 6-digit
                    pairing code shown below into RubyTill Pay on the terminal.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-stone-700 mb-1">Device Name</label>
            <input
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="Front Counter Reader"
              className="w-full px-3 py-2 text-sm text-stone-900 bg-white border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none placeholder-stone-400"
            />
            <p className="text-[11px] text-stone-500 mt-1">Shown in the NMI dashboard and on the physical device after pairing.</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-700 mb-1">Payment Device</label>
            <SearchableDropdown
              options={catalog.map((c) => ({
                value: c.modelName,
                label: c.modelName,
                subtitle: [c.manufacturer, c.paymentProfile?.connectionType].filter(Boolean).join(' · '),
              }))}
              value={model || null}
              onChange={(v) => { setModel(v || ''); setRouteId(''); }}
              placeholder={catalogLoading ? 'Loading payment devices…' : 'Select a payment device'}
              searchPlaceholder="Search payment devices..."
              disabled={catalogLoading || catalog.length === 0}
            />
            {cfg && cfg.id !== 'none' && (
              <p className="text-[11px] text-stone-500 mt-1">
                Registers as <strong className="text-stone-700">{cfg.label}</strong>
                {cfg.profile ? <> · <span className="font-mono">{cfg.profile.connectionType}</span></> : null}
              </p>
            )}
            {noConfig && (
              <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 flex items-start gap-2">
                <AlertCircle size={13} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-[11px] text-amber-800">
                  {cfg?.id === 'none'
                    ? <><span className="font-mono">{model}</span> is checked in with <strong>no payment config</strong>, so
                      there is no lane to register it on. Choose one on the device in Device Management.</>
                    : <><span className="font-mono">{model}</span> has <strong>no payment config</strong>. Until one is set
                      it has no lane — set <em>Payment Device Config</em> on it in Device Management, then come back.</>}
                </p>
              </div>
            )}
          </div>

          {routeOptions.length > 1 && (
            <div>
              <label className="block text-xs font-medium text-stone-700 mb-1">How the till reaches it</label>
              <div role="radiogroup" aria-label="How the till reaches it" className="grid gap-2 sm:grid-cols-3">
                {routeOptions.map((o) => {
                  const on = cfg?.id === o.id;
                  const t = ROUTE_TITLE[o.id] || { title: o.label, desc: '' };
                  return (
                    <button
                      key={o.id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      data-testid={`route-${o.id}`}
                      disabled={!!result || !!rtpayPaired}
                      onClick={() => setRouteId(o.id)}
                      className={`min-h-[44px] rounded-xl border p-2.5 text-left disabled:opacity-50 ${on ? 'border-ruby bg-[#FFF1F3]' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                    >
                      <span className="block text-[13px] font-semibold text-stone-900">{t.title}</span>
                      {t.desc && <span className="mt-0.5 block text-[11px] leading-snug text-stone-500">{t.desc}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-stone-700 mb-1">Serial Number (your internal reference)</label>
            <input
              type="text"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="e.g. PAY-001 or sticker serial"
              className="w-full px-3 py-2 text-sm font-mono text-stone-900 bg-white border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none placeholder-stone-400"
            />
            <p className="text-[11px] text-stone-500 mt-1">
              Read off the device sticker / box. NMI's reported serial is captured separately under <span className="font-mono">NMI Registration Info</span>.
            </p>
          </div>

          {isDejavooUsb && (
            <>
              <div>
                <label className="block text-xs font-medium text-stone-700 mb-1">SPIn Register Id</label>
                <input
                  type="text"
                  value={spinRegisterId}
                  onChange={(e) => setSpinRegisterId(e.target.value)}
                  placeholder="e.g. 9341901"
                  className="w-full px-3 py-2 text-sm font-mono text-stone-900 bg-white border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none placeholder-stone-400"
                />
                <p className="text-[11px] text-stone-500 mt-1">From the terminal's SPIn Integration params → <span className="font-mono">Register Id</span>.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-700 mb-1">SPIn Auth Key</label>
                <input
                  type="text"
                  value={spinAuthKey}
                  onChange={(e) => setSpinAuthKey(e.target.value)}
                  placeholder="e.g. XQtL9Hxfjh"
                  className="w-full px-3 py-2 text-sm font-mono text-stone-900 bg-white border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none placeholder-stone-400"
                />
                <p className="text-[11px] text-stone-500 mt-1">From the terminal's SPIn Integration params → <span className="font-mono">Auth Key</span>. Routes the sale to this terminal via the SPIn cloud (no IP needed).</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-700 mb-1">iPOS Transact TPN</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={spinTpn}
                  onChange={(e) => setSpinTpn(e.target.value.replace(/\D/g, '').slice(0, 12))}
                  placeholder="12-digit TPN, e.g. 220926369218"
                  className="w-full px-3 py-2 text-sm font-mono text-stone-900 bg-white border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none placeholder-stone-400"
                />
                <p className="text-[11px] text-stone-500 mt-1">This terminal's 12-digit <span className="font-mono">TPN</span> (merchant ID). Required for <span className="font-medium">refunds</span> — a SPIn USB terminal can't do a linked refund, so refunds go through the iPOS Transact cloud API keyed to this TPN. Each tenant/terminal has its own.</p>
              </div>
            </>
          )}

          {isRtpay && !rtpayPaired && (
            <RubyTillPayRegisterFields
              tpn={spinTpn}
              onTpnChange={setSpinTpn}
              mode={rtpayMode}
              onModeChange={setRtpayMode}
              pin={rtpayPin}
              onPinChange={setRtpayPin}
              disabled={saving}
            />
          )}
          {isRtpay && rtpayPaired && (
            <RtpayPairingCodePanel
              tenantId={tenantId}
              deviceId={rtpayPaired.deviceId}
              initial={rtpayPaired.initial}
              onClose={onRegistered}
            />
          )}

          {!isDirect && (
            <div>
              <label className="block text-xs font-medium text-stone-700 mb-1">Registration Code (from device screen)</label>
              <input
                type="text"
                value={registrationCode}
                onChange={(e) => setRegistrationCode(e.target.value.toUpperCase().replace(/\s/g, ''))}
                placeholder="8AVJWV"
                maxLength={12}
                autoCapitalize="characters"
                spellCheck={false}
                className="w-full px-3 py-2 text-base font-mono tracking-widest text-stone-900 bg-white border-2 border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none uppercase placeholder-stone-400"
              />
              <p className="text-[11px] text-stone-500 mt-1">
                4–12 uppercase letters/digits. <span className="font-mono">poiDeviceId</span>, NMI serial, and license come back from NMI and are stored automatically.
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
              <AlertCircle size={14} className="text-red-600 shrink-0 mt-0.5" />
              <p className="text-xs text-red-700 whitespace-pre-wrap">{error}</p>
            </div>
          )}

          {result?.poiDeviceId && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
              <div className="flex items-start gap-2">
                <Check size={14} className="text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-xs text-emerald-800 space-y-0.5">
                  <p className="font-semibold">NMI Registration Info</p>
                  <p className="font-mono text-[11px]">poiDeviceId: {result.poiDeviceId}</p>
                  {result.nmiSerialNumber && <p className="font-mono text-[11px]">NMI serial: {result.nmiSerialNumber}</p>}
                  {result.deviceLicense && result.deviceLicense !== result.poiDeviceId && (
                    <p className="font-mono text-[11px]">license: {result.deviceLicense}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-stone-100 bg-stone-50 rounded-b-2xl flex items-center justify-end gap-2">
          {rtpayPaired ? (
            <button
              onClick={onRegistered}
              className="inline-flex min-h-[44px] items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-stone-900 text-white hover:bg-black"
            >
              <Check size={14} /> Done
            </button>
          ) : <>
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm font-medium text-stone-600 hover:text-stone-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave || saving || !!result}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Check size={14} />}
            {saving ? 'Registering…' : result ? 'Paired' : 'Register & Pair'}
          </button>
          </>}
        </div>
      </div>
    </div>
  );
};

export default RegisterPoiModal;
